import { useState } from 'react';
import { X, Loader2, Package, Check, Apple, Sparkles } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { Button, Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useSuggestions } from '../hooks/useSuggestions';
import type { ProductType } from '../types';

interface ProductSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  barcode?: string;
  productType: ProductType;
}

export function ProductSuggestionModal({
  isOpen,
  onClose,
  barcode,
  productType: initialProductType,
}: ProductSuggestionModalProps) {
  const { t } = useTranslation('scanner');
  const { submitProductSuggestion, isSubmitting, error, clearError } = useSuggestions();
  const [success, setSuccess] = useState(false);

  const [brand, setBrand] = useState('');
  const [productName, setProductName] = useState('');
  const [productType, setProductType] = useState<ProductType>(initialProductType);
  const [similarFound, setSimilarFound] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!brand.trim() || !productName.trim() || similarFound === null) {
      return;
    }

    const result = await submitProductSuggestion({
      brand: brand.trim(),
      product_name: productName.trim(),
      product_type: productType,
      barcode: barcode || undefined,
      similar_found: similarFound,
      notes: notes.trim() || undefined,
    });

    if (result) {
      setSuccess(true);
    }
  };

  const handleClose = () => {
    setBrand('');
    setProductName('');
    setProductType(initialProductType);
    setSimilarFound(null);
    setNotes('');
    setSuccess(false);
    clearError();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header - fixed */}
        <div className="flex-shrink-0 bg-white border-b px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('productSuggestion.title')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('productSuggestion.successTitle')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('productSuggestion.successDescription')}
              </p>
              <Button onClick={handleClose}>
                {t('productSuggestion.close')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-gray-600">
                {t('productSuggestion.description')}
              </p>

              {barcode && (
                <div className="bg-gray-100 rounded-lg px-3 py-2">
                  <span className="text-xs text-gray-500">{t('productSuggestion.barcodeLabel')}</span>
                  <span className="text-sm font-mono ml-2">{barcode}</span>
                </div>
              )}

              {/* Product Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('productSuggestion.productType')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setProductType('food')}
                    className={cn(
                      'flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors',
                      productType === 'food'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Apple className="w-5 h-5" />
                    <span className="font-medium">{t('productSuggestion.food')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setProductType('cosmetic')}
                    className={cn(
                      'flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors',
                      productType === 'cosmetic'
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Sparkles className="w-5 h-5" />
                    <span className="font-medium">{t('productSuggestion.cosmetic')}</span>
                  </button>
                </div>
              </div>

              {/* Brand */}
              <div>
                <label htmlFor="brand" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('productSuggestion.brandLabel')}
                </label>
                <Input
                  id="brand"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder={t('productSuggestion.brandPlaceholder')}
                  required
                />
              </div>

              {/* Product Name */}
              <div>
                <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('productSuggestion.productNameLabel')}
                </label>
                <Input
                  id="productName"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder={t('productSuggestion.productNamePlaceholder')}
                  required
                />
              </div>

              {/* Similar Found */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('productSuggestion.similarFound')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSimilarFound(true)}
                    className={cn(
                      'px-4 py-2 rounded-lg border-2 transition-colors font-medium',
                      similarFound === true
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    {t('productSuggestion.yes')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSimilarFound(false)}
                    className={cn(
                      'px-4 py-2 rounded-lg border-2 transition-colors font-medium',
                      similarFound === false
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    {t('productSuggestion.no')}
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('productSuggestion.additionalLabel')}
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('productSuggestion.additionalPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  className="flex-1"
                  onClick={handleClose}
                >
                  {t('productSuggestion.cancel')}
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isSubmitting || !brand.trim() || !productName.trim() || similarFound === null}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('productSuggestion.submitting')}
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4 mr-2" />
                      {t('productSuggestion.submit')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
